/**
 * Market Energy Allocation Utility Functions
 * Handles data processing and calculations for market energy allocation
 */

// Mock data generation for market energy allocation
export const generateMarketEnergyData = (riskLevels, markets) => {
  const data = {};
  
  riskLevels.forEach(risk => {
    data[risk] = markets.map(() => {
      const baseValue = Math.random() * 100;
      const variance = risk === 'High Risk' ? 0.3 : risk === 'Medium Risk' ? 0.2 : 0.1;
      return Math.round(baseValue * (1 + (Math.random() - 0.5) * variance));
    });
  });
  
  return data;
};

// Calculate allocation metrics
export const calculateAllocationMetrics = (data, selectedRisk) => {
  if (!data || typeof data !== 'object') return null;
  
  const riskData = selectedRisk === 'All' ? data : { [selectedRisk]: data[selectedRisk] };
  
  const metrics = {
    totalAllocation: 0,
    averageAllocation: 0,
    maxAllocation: 0,
    minAllocation: Infinity,
    riskDistribution: {}
  };
  
  Object.entries(riskData).forEach(([risk, values]) => {
    if (Array.isArray(values)) {
      const total = values.reduce((sum, val) => sum + val, 0);
      const avg = total / values.length;
      const max = Math.max(...values);
      const min = Math.min(...values);
      
      metrics.totalAllocation += total;
      metrics.maxAllocation = Math.max(metrics.maxAllocation, max);
      metrics.minAllocation = Math.min(metrics.minAllocation, min);
      metrics.riskDistribution[risk] = {
        total,
        average: avg,
        max,
        min
      };
    }
  });
  
  const totalValues = Object.values(metrics.riskDistribution).length;
  metrics.averageAllocation = totalValues > 0 ? metrics.totalAllocation / totalValues : 0;
  
  return metrics;
};

// Format allocation values
export const formatAllocation = (value, unit = 'MWh') => {
  if (typeof value !== 'number') return '0 ' + unit;
  
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k ${unit}`;
  }
  
  return `${value.toFixed(0)} ${unit}`;
};

// Validate market energy data
export const validateMarketData = (data, riskLevels, markets) => {
  const errors = [];
  
  if (!data || typeof data !== 'object') {
    errors.push('Data must be an object');
    return { isValid: false, errors };
  }
  
  riskLevels.forEach(risk => {
    if (!data[risk]) {
      errors.push(`Missing data for risk level: ${risk}`);
    } else if (!Array.isArray(data[risk])) {
      errors.push(`Data for ${risk} must be an array`);
    } else if (data[risk].length !== markets.length) {
      errors.push(`Data for ${risk} must have ${markets.length} values`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Get chart configuration
export const getChartConfig = (data, config, selectedRisk) => {
  if (!data || !config) return {};
  
  const { riskLevels, markets, colors, chart } = config;
  
  return {
    tooltip: chart.tooltip,
    legend: {
      ...chart.legend,
      data: riskLevels
    },
    grid: chart.grid,
    xAxis: {
      type: 'category',
      data: markets,
      axisTick: { alignWithLabel: true },
      axisLabel: {
        fontSize: 11,
        color: '#666'
      }
    },
    yAxis: {
      type: 'value',
      name: chart.yAxisLabel,
      nameTextStyle: {
        fontSize: 12,
        color: '#666'
      },
      axisLabel: {
        fontSize: 11,
        color: '#666'
      }
    },
    series: riskLevels.map((risk) => ({
      name: risk,
      type: 'bar',
      stack: 'total',
      emphasis: {
        focus: 'series',
      },
      data: data[risk] || [],
      itemStyle: {
        color: colors[risk],
        opacity: selectedRisk === risk ? 1 : 0.7
      }
    }))
  };
};

export default {
  generateMarketEnergyData,
  calculateAllocationMetrics,
  formatAllocation,
  validateMarketData,
  getChartConfig
};
